import { useState } from "react";
import {
  useGetPrePumpWatchlistQuery,
  useTickPrePumpWatchlistNowMutation,
} from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtTime } from "@/lib/format";
import { toast } from "sonner";

/**
 * PrePumpWatchlistCard — shows the "stalk-before-commit" queue.
 *
 * Tabs:
 *   watching     — currently being monitored, not yet fired
 *   confirmed    — graduated to a real buy
 *   expired      — aged out (4h) without confirmation
 *   invalidated  — score crashed / price faded / vol dropped
 *
 * Each row: symbol, status, age, watch price, initial vs current
 * score, vol, removed reason (when applicable), fire details.
 */
export function PrePumpWatchlistCard() {
  const [tab, setTab] = useState<"watching" | "confirmed" | "expired" | "invalidated">("watching");
  const list = useGetPrePumpWatchlistQuery(tab, { pollingInterval: 30000 });
  const [tickNow, { isLoading: ticking }] = useTickPrePumpWatchlistNowMutation();

  async function onTick() {
    try {
      const r: any = await tickNow().unwrap();
      const a = r?.actions || {};
      const summary = `added ${r?.new_added ?? 0}, fired ${a?.fired?.length ?? 0}, ` +
                      `expired ${a?.expired?.length ?? 0}, invalidated ${a?.invalidated?.length ?? 0}`;
      toast.success(`tick done — ${summary}`);
    } catch (e: any) {
      toast.error(`tick failed: ${e?.data?.detail ?? "?"}`);
    }
  }

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-2 flex-wrap">
        <CardTitle className="flex items-baseline gap-2 flex-wrap">
          <span>Watchlist</span>
          <span className="text-xs text-muted font-normal">
            stalk-before-commit · 10 min min watch · 4h max · fires on continuation
          </span>
        </CardTitle>
        <div className="flex items-center gap-1">
          <div className="flex rounded border overflow-hidden">
            {(["watching","confirmed","expired","invalidated"] as const).map((s) => (
              <button key={s}
                      className={`text-xs px-2 py-1 ${tab === s
                        ? "bg-accent/20 text-fg font-semibold" : "bg-bg text-muted hover:bg-muted/20"}`}
                      onClick={() => setTab(s)}>
                {s}
              </button>
            ))}
          </div>
          <Button size="sm" variant="ghost" onClick={onTick} disabled={ticking}>
            {ticking ? "Ticking…" : "Tick now"}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {list.isLoading && <Skeleton className="h-24 w-full" />}
        {list.data && list.data.length === 0 && (
          <div className="text-sm text-muted">
            {tab === "watching" && "No active watches. New candidates appear here when detector finds score≥90 Pattern B vol×≥2 setups."}
            {tab === "confirmed" && "No graduated watches yet — these become real BUYs once stalk-period passes all checks."}
            {tab === "expired" && "No expired watches yet — these aged out (4h) without continuation."}
            {tab === "invalidated" && "No invalidated watches — these had score/price/vol degrade."}
          </div>
        )}
        {list.data && list.data.length > 0 && (
          <Table>
            <THead>
              <TR>
                <TH>Symbol</TH>
                <TH>Status</TH>
                <TH>Added</TH>
                <TH className="text-right">Watch $</TH>
                <TH className="text-right">Init score</TH>
                <TH className="text-right">Best seen</TH>
                <TH className="text-right">Vol×</TH>
                <TH className="text-right">Checks</TH>
                {tab !== "watching" && <TH>Removed reason</TH>}
                {tab === "confirmed" && <TH className="text-right">Fire $</TH>}
              </TR>
            </THead>
            <TBody>
              {list.data.map((r: any) => {
                const tone = r.status === "confirmed" ? "good"
                          : r.status === "watching"   ? "info"
                          : r.status === "expired"    ? "muted"
                                                       : "warn";
                return (
                  <TR key={r.id}>
                    <TD className="font-mono font-semibold">{r.symbol}</TD>
                    <TD><Badge tone={tone}>{r.status}</Badge></TD>
                    <TD className="text-xs whitespace-nowrap">{fmtTime(r.added_ts)}</TD>
                    <TD className="text-right tabular-nums font-mono text-xs">
                      {Number(r.watch_price).toFixed(r.watch_price >= 1 ? 4 : 8)}
                    </TD>
                    <TD className="text-right tabular-nums">{Number(r.initial_score).toFixed(0)}</TD>
                    <TD className="text-right tabular-nums">
                      {r.best_score_seen ? Number(r.best_score_seen).toFixed(0) : "—"}
                    </TD>
                    <TD className="text-right tabular-nums">
                      {r.initial_vol_expansion ? `${Number(r.initial_vol_expansion).toFixed(1)}x` : "—"}
                    </TD>
                    <TD className="text-right tabular-nums text-xs">{r.checks_done ?? 0}</TD>
                    {tab !== "watching" && (
                      <TD className="text-xs text-muted max-w-xs truncate" title={r.removed_reason ?? ""}>
                        {r.removed_reason ?? "—"}
                      </TD>
                    )}
                    {tab === "confirmed" && (
                      <TD className="text-right tabular-nums font-mono text-xs">
                        {r.fire_price ? Number(r.fire_price).toFixed(r.fire_price >= 1 ? 4 : 8) : "—"}
                      </TD>
                    )}
                  </TR>
                );
              })}
            </TBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
